import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Badge,
  StatusIndicator,
  Button,
  PropertyFilter,
  ColumnLayout,
  Link,
  Pagination,
} from '@cloudscape-design/components';
import { getAllFeedback, resolveFeedback } from '../services/s3Service';
import { getFeedbackSnapshot, saveFeedbackSnapshot } from '../services/feedbackCache';
import { useHistory } from 'react-router-dom';
import { getFundRequestDetailsUrl } from '../utils/urlHelper';
import { getProgramConfig } from '../programs/registry';
import {
  getFeedbackStatus,
  getReplyDispositionBadgeColor,
  getReplyDispositionLabel,
  isActionableReplyDisposition,
} from '../utils/feedbackUtils';

const DECISION_LABELS = {
  recommend_approval: 'Recommend Approval',
  recommend_rejection: 'Recommend Rejection',
  needs_review: 'Needs Review',
};

const PAGE_SIZE = 10;

const exportFeedbackCsv = (items) => {
  const headers = [
    'Fund Request ID', 'Reviewer', 'Chronos Decision', 'Feedback',
    'Recommended Decision', 'Disagreed Validations', 'Reason',
    'Reply Disposition', 'SIM-T URL', 'Status', 'Submitted',
  ];
  const rows = items.map((item) => [
    item.fundRequestId || '',
    item.reviewerAlias || '',
    DECISION_LABELS[item.validationDecision] || item.validationDecision || '',
    item.feedbackType || '',
    DECISION_LABELS[item.recommendedDecision] || item.recommendedDecision || '',
    (item.disagreementValidations || []).join('; '),
    item.disagreementReason || '',
    getReplyDispositionLabel(item.replyDisposition),
    item.simTicketUrl || '',
    getFeedbackStatus(item),
    item.submittedAt || '',
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `feedback-report-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const FeedbackReport = () => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canReply, setCanReply] = useState(false);
  // No default token: the summary cards describe the filtered rows, so a preset
  // Status filter would report 0 resolved, 0 agree and a 0% agreement rate on load.
  const [filterQuery, setFilterQuery] = useState({ tokens: [], operation: 'and' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedReasons, setExpandedReasons] = useState(new Set());
  const [sortingColumn, setSortingColumn] = useState({ sortingField: 'submittedAt' });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [resolvingItems, setResolvingItems] = useState(new Set());
  const history = useHistory();

  // Keep current data + view state in a ref so the unmount handler can snapshot
  // it — mirrors Event History: re-entry restores the exact last view.
  const snapStateRef = useRef({});
  snapStateRef.current = {
    feedback: feedbackItems, isAdmin, canReply,
    filterQuery, sortingColumn, sortingDescending, currentPage,
  };

  useEffect(() => {
    const snap = getFeedbackSnapshot();
    if (snap) {
      // Instant restore of data AND view state (filter/sort/page); revalidate quietly.
      setFeedbackItems(snap.feedback || []);
      setIsAdmin(snap.isAdmin || false);
      setCanReply(snap.canReply || false);
      if (snap.filterQuery) setFilterQuery(snap.filterQuery);
      if (snap.sortingColumn) setSortingColumn(snap.sortingColumn);
      if (typeof snap.sortingDescending === 'boolean') setSortingDescending(snap.sortingDescending);
      if (snap.currentPage) setCurrentPage(snap.currentPage);
      setLoading(false);
      revalidate();
    } else {
      loadFeedback();
    }
    // Save the snapshot on unmount (route change). Skip empty (pre-first-load).
    return () => {
      const s = snapStateRef.current;
      if (s.feedback && s.feedback.length > 0) saveFeedbackSnapshot(s);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyData = (data) => {
    setFeedbackItems(data.feedback || []);
    setIsAdmin(data.isAdmin || false);
    setCanReply(data.canReplyToFeedback || false);
  };

  const loadFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllFeedback();
      applyData(data);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Background refresh after an instant snapshot restore — no spinner; failures swallowed.
  const revalidate = async () => {
    try {
      const data = await getAllFeedback();
      applyData(data);
    } catch (err) {
      console.debug('Feedback revalidation failed:', err);
    }
  };

  const handleResolve = async (fundRequestId, reviewerId) => {
    const itemKey = `${fundRequestId}:${reviewerId}`;
    setResolvingItems((prev) => new Set(prev).add(itemKey));
    try {
      await resolveFeedback(fundRequestId, reviewerId);
      await loadFeedback();
    } catch (err) {
      console.error('Failed to resolve feedback:', err);
    } finally {
      setResolvingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  // Display-value getters per filter property — also build the PropertyFilter
  // option lists below, so filtering and option values stay in sync.
  const propStatus = (i) => getFeedbackStatus(i);
  const propProgram = (i) => {
    const code = String(i.program || '').toUpperCase();
    return getProgramConfig(code)?.shortName || code;
  };
  const propFeedback = (i) => (i.feedbackType === 'AGREE' ? 'Agree' : i.feedbackType === 'DISAGREE' ? 'Disagree' : '');
  const propReviewer = (i) => i.reviewerAlias || '';
  const propDecision = (i) => DECISION_LABELS[i.validationDecision] || i.validationDecision || '';
  const propReply = (item) => {
    if (item.replyDisposition) return getReplyDispositionLabel(item.replyDisposition);
    if (item.feedbackType === 'DISAGREE') return 'Pending';
    return '';
  };
  const PROP_GETTERS = {
    status: propStatus,
    program: propProgram,
    feedbackType: propFeedback,
    reviewerAlias: propReviewer,
    validationDecision: propDecision,
    replyDisposition: propReply,
  };

  // One unified PropertyFilter (matches Event History). Reviewer is admin-only.
  const filteringProperties = [
    { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['=', '!='] },
    { key: 'program', propertyLabel: 'Program', groupValuesLabel: 'Program values', operators: ['=', '!='] },
    { key: 'feedbackType', propertyLabel: 'Feedback', groupValuesLabel: 'Feedback values', operators: ['=', '!='] },
    ...(isAdmin ? [{ key: 'reviewerAlias', propertyLabel: 'Reviewer', groupValuesLabel: 'Reviewer values', operators: ['=', '!=', ':'] }] : []),
    { key: 'validationDecision', propertyLabel: 'Chronos Decision', groupValuesLabel: 'Decision values', operators: ['=', '!='] },
    { key: 'replyDisposition', propertyLabel: 'Reply', groupValuesLabel: 'Reply values', operators: ['=', '!='] },
  ];

  // Value suggestions derived from the loaded data (self-maintaining).
  const filteringOptions = (() => {
    const out = [];
    const seen = new Set();
    const add = (key, val) => {
      if (!val) return;
      const k = `${key}\u0000${val}`;
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ propertyKey: key, value: val });
    };
    feedbackItems.forEach((i) => {
      add('status', propStatus(i));
      add('program', propProgram(i));
      add('feedbackType', propFeedback(i));
      if (isAdmin) add('reviewerAlias', propReviewer(i));
      add('validationDecision', propDecision(i));
      add('replyDisposition', propReply(i));
    });
    return out;
  })();

  const filteredItems = feedbackItems.filter((item) => {
    const { tokens = [], operation = 'and' } = filterQuery || {};
    if (tokens.length === 0) return true;
    const checks = tokens.map((t) => {
      const get = PROP_GETTERS[t.propertyKey];
      if (!get) return true;
      const v = String(get(item) ?? '');
      const val = String(t.value);
      if (t.operator === '!=') return v !== val;
      if (t.operator === ':') return v.toLowerCase().includes(val.toLowerCase());
      if (t.operator === '!:') return !v.toLowerCase().includes(val.toLowerCase());
      return v === val;
    });
    return operation === 'or' ? checks.some(Boolean) : checks.every(Boolean);
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    const field = sortingColumn.sortingField;
    const aVal = a[field] || '';
    const bVal = b[field] || '';
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortingDescending ? -cmp : cmp;
  });
  const totalPages = Math.max(Math.ceil(sortedItems.length / PAGE_SIZE), 1);
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const paginatedItems = sortedItems.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const resetPage = () => setCurrentPage(1);

  const agreeCount = filteredItems.filter((i) => i.feedbackType === 'AGREE').length;
  const disagreeCount = filteredItems.filter((i) => i.feedbackType === 'DISAGREE').length;
  const total = filteredItems.length;
  const agreementRate = total > 0 ? Math.round((agreeCount / total) * 100) : 0;
  const openCount = filteredItems.filter((i) => getFeedbackStatus(i) === 'Open').length;
  const resolvedCount = filteredItems.filter((i) => getFeedbackStatus(i) === 'Resolved').length;
  const uniqueReviewers = isAdmin ? new Set(filteredItems.map((i) => i.reviewerAlias)).size : null;

  const formatDecision = (value) => DECISION_LABELS[value] || value || '—';

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <Container header={<Header variant="h1">Feedback Report</Header>}>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="error">{error}</StatusIndicator>
          <Box padding={{ top: 'm' }}>
            <Button onClick={loadFeedback}>Retry</Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Build column definitions — add Reviewer column for admins
  const columnDefinitions = [
    {
      id: 'fundRequestId',
      header: 'Fund Request ID',
      cell: (item) => (
        <Link
          href={getFundRequestDetailsUrl(item.fundRequestId)}
          external
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.fundRequestId || '—'}
        </Link>
      ),
      sortingField: 'fundRequestId',
      width: 220,
    },
    ...(isAdmin
      ? [
          {
            id: 'reviewerAlias',
            header: 'Reviewer',
            cell: (item) => item.reviewerAlias || '—',
          },
        ]
      : []),
    {
      id: 'validationDecision',
      header: 'Chronos Decision',
      cell: (item) => formatDecision(item.validationDecision),
    },
    {
      id: 'feedbackType',
      header: 'Feedback',
      cell: (item) => (
        <Badge color={item.feedbackType === 'AGREE' ? 'green' : 'red'}>
          {item.feedbackType}
        </Badge>
      ),
      width: 110,
    },
    {
      id: 'recommendedDecision',
      header: 'Recommended Decision',
      cell: (item) => formatDecision(item.recommendedDecision),
    },
    {
      id: 'disagreementValidations',
      header: 'Disagreed Validations',
      cell: (item) => {
        const validations = item.disagreementValidations || [];
        if (validations.length === 0) return '—';
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {validations.map((v) => (
              <Badge key={v} color="red">
                {v.replace(/_/g, ' ')}
              </Badge>
            ))}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'disagreementReason',
      header: 'Reason',
      cell: (item) => {
        const reason = item.disagreementReason;
        if (!reason) return '—';
        const MAX_LEN = 120;
        if (reason.length <= MAX_LEN) return reason;
        const itemKey = `${item.fundRequestId}:${item.reviewerId}`;
        const isExpanded = expandedReasons.has(itemKey);
        const toggle = () => {
          setExpandedReasons((prev) => {
            const next = new Set(prev);
            if (next.has(itemKey)) {
              next.delete(itemKey);
            } else {
              next.add(itemKey);
            }
            return next;
          });
        };
        return (
          <Box>
            {isExpanded ? reason : `${reason.substring(0, MAX_LEN)}...`}
            {' '}
            <Link onFollow={(e) => { e.preventDefault(); toggle(); }} variant="primary" fontSize="body-s">
              {isExpanded ? 'Show less' : 'Show more'}
            </Link>
          </Box>
        );
      },
    },
    {
      id: 'replyStatus',
      header: 'Reply',
      cell: (item) => {
        if (item.feedbackType !== 'DISAGREE') return '—';
        if (item.replyDisposition) {
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color={getReplyDispositionBadgeColor(item.replyDisposition)}>
                {getReplyDispositionLabel(item.replyDisposition)}
              </Badge>
              {item.simTicketUrl && (
                <Link href={item.simTicketUrl} external variant="primary" target="_blank" rel="noopener noreferrer">
                  View SIM-T
                </Link>
              )}
            </SpaceBetween>
          );
        }
        if (canReply) {
          return (
            <Button
              variant="normal"
              onClick={() => history.push(getFundRequestDetailsUrl(item.fundRequestId))}
            >
              Reply
            </Button>
          );
        }
        return <Badge color="grey">Pending</Badge>;
      },
    },
    {
      id: 'submittedAt',
      header: 'Submitted',
      cell: (item) => formatDate(item.submittedAt),
      sortingField: 'submittedAt',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => {
        const status = getFeedbackStatus(item);
        if (status === 'Resolved') {
          return <Badge color="green">Resolved</Badge>;
        }
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Badge color="red">Open</Badge>
            {canReply && isActionableReplyDisposition(item.replyDisposition) && (
              <Button
                variant="inline-link"
                loading={resolvingItems.has(`${item.fundRequestId}:${item.reviewerId}`)}
                onClick={() => handleResolve(item.fundRequestId, item.reviewerId)}
              >
                Mark Resolved
              </Button>
            )}
          </SpaceBetween>
        );
      },
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1">
        Feedback Report {isAdmin && <Badge color="blue">Admin View</Badge>}
      </Header>

      {/* Summary Cards */}
      <Container>
        <ColumnLayout columns={isAdmin ? 7 : 6} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total Reviews</Box>
            <Box variant="awsui-value-large">{total}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Open</Box>
            <Box variant="awsui-value-large" color="text-status-error">{openCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Resolved</Box>
            <Box variant="awsui-value-large" color="text-status-success">{resolvedCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Agree</Box>
            <Box variant="awsui-value-large" color="text-status-success">{agreeCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Disagree</Box>
            <Box variant="awsui-value-large" color="text-status-warning">{disagreeCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Agreement Rate</Box>
            <Box variant="awsui-value-large">{agreementRate}%</Box>
          </div>
          {isAdmin && (
            <div>
              <Box variant="awsui-key-label">Reviewers</Box>
              <Box variant="awsui-value-large">{uniqueReviewers}</Box>
            </div>
          )}
        </ColumnLayout>
      </Container>

      {/* Feedback Table */}
      <Table
        header={
          <Header
            variant="h2"
            counter={`(${filteredItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={loadFeedback} loading={loading}>
                  Refresh
                </Button>
                <Button iconName="download" onClick={() => exportFeedbackCsv(sortedItems)}>
                  Export CSV
                </Button>
              </SpaceBetween>
            }
          >
            Feedback History
          </Header>
        }
        loading={loading}
        loadingText="Loading feedback..."
        resizableColumns
        wrapLines
        items={paginatedItems}
        empty={
          <Box textAlign="center" padding="xxl">
            <Box variant="h3" color="text-status-inactive">No feedback submitted yet</Box>
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Review fund requests and submit feedback to see your history here.
            </Box>
          </Box>
        }
        filter={
          <PropertyFilter
            query={filterQuery}
            onChange={({ detail }) => { setFilterQuery(detail); resetPage(); }}
            filteringProperties={filteringProperties}
            filteringOptions={filteringOptions}
            hideOperations
            filteringPlaceholder="Filter feedback"
            i18nStrings={{
              filteringAriaLabel: 'Filter feedback',
              dismissAriaLabel: 'Dismiss',
              filteringPlaceholder: 'Filter feedback',
              groupValuesText: 'Values', groupPropertiesText: 'Properties', operatorsText: 'Operators',
              operatorContainsText: 'Contains', operatorDoesNotContainText: 'Does not contain',
              operatorEqualsText: 'Equals', operatorDoesNotEqualText: 'Does not equal',
              editTokenHeader: 'Edit filter', propertyText: 'Property',
              operatorText: 'Operator', valueText: 'Value',
              cancelActionText: 'Cancel', applyActionText: 'Apply',
              allPropertiesLabel: 'All properties',
              tokenLimitShowMore: 'Show more', tokenLimitShowFewer: 'Show fewer',
              clearFiltersText: 'Clear filters',
              removeTokenButtonAriaLabel: (token) => `Remove filter, ${token.propertyKey} ${token.operator} ${token.value}`,
              enteredTextLabel: (text) => `Use: "${text}"`,
            }}
          />
        }
        columnDefinitions={columnDefinitions}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending);
          setCurrentPage(1);
        }}
        pagination={
          totalPages > 1 ? (
            <Pagination
              currentPageIndex={safePage}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          ) : null
        }
      />
    </SpaceBetween>
  );
};

export default FeedbackReport;
